import React, { useCallback, useRef, useState } from "react";
import { LockIcon } from "./icons";
import { t } from "../../utils/i18n";

const HOLD_DURATION_MS = 2500;
const MIN_DRAG_PX = 60;   // tirage minimal vers le bas avant que le maintien ne s'arme
const MAX_UPWARD_PX = 15; // au-delà, ce n'est plus une descente franche : on annule

// Remplace l'ancien "faire défiler 220vh de vide" (rejeté explicitement par le fondateur — "
// descendre dans une page vide n'a aucun sens") par un vrai geste façon archives WhatsApp :
// poser le doigt/curseur sur cette zone dédiée, tirer vers le bas, GARDER la pression sans
// glisser comme un scroll classique, pendant ~2.5s — les discussions masquées apparaissent
// alors (via onComplete, qui déclenche exactement le même flux PIN qu'avant, inchangé).
// Un relâchement à tout moment avant la fin annule proprement, sans état résiduel.
export function HiddenChatsHandle({ lang, onComplete }: { lang: string; onComplete: () => void }) {
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startY = useRef<number | null>(null);
  const startT = useRef(0);
  const rafId = useRef<number | null>(null);
  const armed = useRef(false);

  const reset = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = null;
    startY.current = null;
    armed.current = false;
    setDragging(false);
    setProgress(0);
  }, []);

  const tick = useCallback(() => {
    const elapsed = performance.now() - startT.current;
    const p = Math.min(1, elapsed / HOLD_DURATION_MS);
    setProgress(p);
    if (p >= 1) { onComplete(); reset(); return; }
    rafId.current = requestAnimationFrame(tick);
  }, [onComplete, reset]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    startY.current = e.clientY;
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (startY.current === null) return;
    const dy = e.clientY - startY.current;
    if (dy < -MAX_UPWARD_PX) { reset(); return; }
    if (!armed.current && dy >= MIN_DRAG_PX) {
      armed.current = true;
      startT.current = performance.now();
      rafId.current = requestAnimationFrame(tick);
    }
  };

  const onPointerUp = () => reset();

  const circumference = 2 * Math.PI * 12;

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: "none", padding: "18px 16px 30px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "grab", userSelect: "none" }}
    >
      {/* Anneau de progression façon "pull to refresh" — retour visuel continu pendant le
          maintien, pour que l'effet soit visible avant même le relâchement. */}
      <div style={{ position: "relative", width: 28, height: 28 }}>
        <svg width="28" height="28" viewBox="0 0 28 28" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="14" cy="14" r="12" fill="none" stroke="#eee" strokeWidth="2.5" />
          <circle
            cx="14" cy="14" r="12" fill="none" stroke="#0a0a0a" strokeWidth="2.5"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
            style={{ transition: dragging ? "none" : "stroke-dashoffset 0.15s" }}
          />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", transform: `scale(${1 + progress * 0.25})` }}>
          <LockIcon size={12} color={progress > 0 ? "#0a0a0a" : "#ddd"} />
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: progress > 0 ? "#0a0a0a" : "#ddd" }}>
        {progress >= 1 ? "" : progress > 0 ? (t(lang, "msg_holding") || "Maintenez...") : (t(lang, "msg_hold_to_reveal") || "Tirer pour les discussions masquées")}
      </span>
    </div>
  );
}
